import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Grid,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Pagination,
  Collapse,
  IconButton,
  Tooltip,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  PieChart as PieChartIcon,
  BarChart as BarChartIcon,
  Assessment as AssessmentIcon,
  Business as BusinessIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import ReactECharts from 'echarts-for-react';
import {
  getDashboardDemographics,
  DashboardDemographicsResponse,
  DashboardDemographicsAggregate,
  CategoryWithCount,
  DemographicsEntityRef,
} from '../../services/api';

interface AttributeDemographicsTabProps {
  parentEntityId: number;
  isLeafEntity: boolean;
  selectedCategory?: string | null;
  availableCategories?: CategoryWithCount[];
  onCategoryChange?: (category: string) => void;
}

const DEFAULT_CATEGORY = 'PROJECT';
const NOT_SPECIFIED_LABEL = 'Not Specified';
const ENTITY_TOOLTIP_MAX = 25;

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Bold value label on top; optional count line; then one entity name per line (HTML for ECharts). */
function buildDistributionTooltipHtml(
  valueLabel: string,
  count: number,
  percentText: string | null,
  refs: DemographicsEntityRef[] | undefined,
): string {
  const parts: string[] = [`<b>${escapeHtml(valueLabel)}</b>`];
  const statLine =
    percentText !== null && percentText !== undefined
      ? `${count} (${percentText})`
      : String(count);
  parts.push(escapeHtml(statLine));
  if (refs?.length) {
    refs.slice(0, ENTITY_TOOLTIP_MAX).forEach((r) => {
      parts.push(`• ${escapeHtml(r.display_label || `ID ${r.review_entity_id}`)}`);
    });
    if (refs.length > ENTITY_TOOLTIP_MAX) {
      parts.push(`• ${escapeHtml(`… +${refs.length - ENTITY_TOOLTIP_MAX} more`)}`);
    }
  }
  return parts.join('<br/>');
}

function entitiesByValueFromAggregate(
  agg: DashboardDemographicsAggregate,
): Record<string, DemographicsEntityRef[]> | undefined {
  const raw = agg?.entities_by_value;
  if (!raw || typeof raw !== 'object') return undefined;
  return raw as Record<string, DemographicsEntityRef[]>;
}

function distributionRecordFromAggregate(
  agg: DashboardDemographicsAggregate,
): Record<string, number> | null {
  const t = agg?.type as string | undefined;
  if (t === 'categorical_counts' && agg.distribution && typeof agg.distribution === 'object') {
    return agg.distribution as Record<string, number>;
  }
  if (t === 'text_values' && agg.distinct_values && typeof agg.distinct_values === 'object') {
    return agg.distinct_values as Record<string, number>;
  }
  return null;
}

type BinRow = { name: string; value: number; entities?: DemographicsEntityRef[] };

function binsFromAggregate(agg: DashboardDemographicsAggregate): BinRow[] {
  const bins = agg?.bins as
    | Array<{ range?: string; count?: number; entities?: DemographicsEntityRef[] }>
    | undefined;
  if (!Array.isArray(bins)) return [];
  return bins.map((b) => ({
    name: b.range ?? '—',
    value: typeof b.count === 'number' ? b.count : 0,
    entities: b.entities,
  }));
}

function notSpecifiedBlock(
  agg: DashboardDemographicsAggregate,
): { count: number; entities: DemographicsEntityRef[] } | null {
  const ns = agg?.not_specified as
    | { count?: number; entities?: DemographicsEntityRef[] }
    | undefined;
  if (!ns || typeof ns.count !== 'number' || ns.count <= 0) return null;
  return { count: ns.count, entities: ns.entities ?? [] };
}

/** Pie slices for numeric/percentage: bin ranges plus optional Not Specified. */
function mergeNumericBinsForPie(agg: DashboardDemographicsAggregate): {
  distribution: Record<string, number>;
  entitiesByValue: Record<string, DemographicsEntityRef[]>;
} | null {
  const binRows = binsFromAggregate(agg);
  const ns = notSpecifiedBlock(agg);

  const distribution: Record<string, number> = {};
  const entitiesByValue: Record<string, DemographicsEntityRef[]> = {};

  for (const row of binRows) {
    if (row.value > 0) {
      distribution[row.name] = row.value;
      entitiesByValue[row.name] = row.entities ?? [];
    }
  }
  if (ns) {
    distribution[NOT_SPECIFIED_LABEL] = ns.count;
    entitiesByValue[NOT_SPECIFIED_LABEL] = ns.entities;
  }

  if (Object.keys(distribution).length === 0) return null;
  return { distribution, entitiesByValue };
}

/** 100% Not Specified (or empty aggregate). */
function isNoUsableValueOnly(agg: DashboardDemographicsAggregate): boolean {
  const dist = distributionRecordFromAggregate(agg);
  if (dist && Object.keys(dist).length > 0) {
    const total = Object.values(dist).reduce((a, b) => a + b, 0);
    if (total === 0) return true;
    const ns = dist[NOT_SPECIFIED_LABEL] ?? 0;
    return ns >= total;
  }
  const num = mergeNumericBinsForPie(agg);
  if (num) {
    const total = Object.values(num.distribution).reduce((a, b) => a + b, 0);
    if (total === 0) return true;
    const ns = num.distribution[NOT_SPECIFIED_LABEL] ?? 0;
    return ns >= total;
  }
  return true;
}

function specifiedFraction(agg: DashboardDemographicsAggregate): number {
  const dist = distributionRecordFromAggregate(agg);
  if (dist && Object.keys(dist).length > 0) {
    const total = Object.values(dist).reduce((a, b) => a + b, 0);
    if (total === 0) return 0;
    const ns = dist[NOT_SPECIFIED_LABEL] ?? 0;
    return (total - ns) / total;
  }
  const num = mergeNumericBinsForPie(agg);
  if (num) {
    const total = Object.values(num.distribution).reduce((a, b) => a + b, 0);
    if (total === 0) return 0;
    const ns = num.distribution[NOT_SPECIFIED_LABEL] ?? 0;
    return (total - ns) / total;
  }
  return 0;
}

function nonNotSpecifiedDistinctCount(agg: DashboardDemographicsAggregate): number {
  const dist =
    distributionRecordFromAggregate(agg) ?? mergeNumericBinsForPie(agg)?.distribution ?? null;
  if (!dist) return 0;
  return Object.entries(dist).filter(([k, v]) => v > 0 && k !== NOT_SPECIFIED_LABEL).length;
}

function insightScore(agg: DashboardDemographicsAggregate): number {
  if (isNoUsableValueOnly(agg)) return -1;
  return specifiedFraction(agg) * 100 + nonNotSpecifiedDistinctCount(agg) * 5;
}

const PAGE_SIZE = 6;
const TOP_INSIGHTS_COUNT = 3;

const AttributeDemographicsTab: React.FC<AttributeDemographicsTabProps> = ({
  parentEntityId,
  isLeafEntity: _isLeafProp,
  selectedCategory,
  availableCategories = [],
  onCategoryChange,
}) => {
  const [demoData, setDemoData] = useState<DashboardDemographicsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');
  const [entityTypeSubTab, setEntityTypeSubTab] = useState(0);
  const [summaryExpanded, setSummaryExpanded] = useState(false);
  const [attrPage, setAttrPage] = useState(1);
  const [attributeSearch, setAttributeSearch] = useState('');
  const [hideNoUsableSection, setHideNoUsableSection] = useState(false);
  const [noUsableExpanded, setNoUsableExpanded] = useState(false);
  const theme = useTheme();

  const effectiveCategory =
    (selectedCategory && selectedCategory.trim()) ||
    (availableCategories[0]?.category ?? DEFAULT_CATEGORY);

  useEffect(() => {
    if (!parentEntityId) return;

    const run = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await getDashboardDemographics(parentEntityId, effectiveCategory);
        setDemoData(response.data);
      } catch (err: unknown) {
        console.error('Error fetching dashboard demographics:', err);
        setError('Failed to load demographics data');
        setDemoData(null);
      } finally {
        setLoading(false);
      }
    };

    run();
  }, [parentEntityId, effectiveCategory]);

  const groupKeysSignature = demoData?.groups
    ? Object.keys(demoData.groups).sort().join('|')
    : '';

  useEffect(() => {
    setEntityTypeSubTab(0);
  }, [parentEntityId, effectiveCategory, groupKeysSignature]);

  useEffect(() => {
    setAttrPage(1);
  }, [parentEntityId, effectiveCategory, groupKeysSignature, entityTypeSubTab, attributeSearch]);

  const groups = demoData?.groups;
  const sortedGroupNames = useMemo(
    () => (groups ? Object.keys(groups).sort() : []),
    [groups],
  );

  const tabAttributeBuckets = useMemo(() => {
    type TabAttr = {
      name: string;
      display_label: string;
      sample_size: number;
      aggregate: DashboardDemographicsAggregate;
    };
    const empty = {
      topInsights: [] as TabAttr[],
      filteredUsable: [] as TabAttr[],
      filteredUnusable: [] as TabAttr[],
    };
    if (!groups || sortedGroupNames.length === 0) {
      return empty;
    }
    const idx = Math.min(entityTypeSubTab, sortedGroupNames.length - 1);
    const gname = sortedGroupNames[idx];
    const ag = groups[gname];
    const attrs = ag?.attributes ?? [];
    const usable = attrs.filter((a) => !isNoUsableValueOnly(a.aggregate));
    const unusable = attrs.filter((a) => isNoUsableValueOnly(a.aggregate));
    const topInsights = [...usable]
      .sort((a, b) => insightScore(b.aggregate) - insightScore(a.aggregate))
      .slice(0, TOP_INSIGHTS_COUNT);
    const topInsightNames = new Set(topInsights.map((a) => a.name));
    const q = attributeSearch.trim().toLowerCase();
    const match = (label: string) => !q || label.toLowerCase().includes(q);
    const filteredUsable = usable
      .filter((a) => !topInsightNames.has(a.name))
      .filter((a) => match(a.display_label));
    const filteredUnusable = unusable.filter((a) => match(a.display_label));
    return { topInsights, filteredUsable, filteredUnusable };
  }, [groups, sortedGroupNames, entityTypeSubTab, attributeSearch]);

  const getDemographicsPieOption = (
    distribution: Record<string, number>,
    title: string,
    entitiesByValue?: Record<string, DemographicsEntityRef[]>,
    compact = false,
  ) => {
    const chartData = Object.entries(distribution).map(([name, value]) => ({
      name,
      value,
    }));

    return {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: compact ? 11 : 14,
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      },
      tooltip: {
        trigger: 'item',
        renderMode: 'html',
        formatter: (params: { name: string; value: number; percent: number }) =>
          buildDistributionTooltipHtml(
            params.name,
            params.value,
            `${params.percent}%`,
            entitiesByValue?.[params.name],
          ),
      },
      legend: {
        show: true,
        orient: 'horizontal',
        left: 'center',
        bottom: compact ? 4 : 8,
        type: chartData.length > 8 ? 'scroll' : 'plain',
        data: chartData.map((d) => d.name),
        itemGap: 10,
        itemWidth: 12,
        itemHeight: 10,
        textStyle: {
          fontSize: compact ? 10 : 12,
          color: theme.palette.text.primary,
        },
        formatter: (name: string) => (name === '' ? '—' : name),
      },
      series: [
        {
          type: 'pie',
          radius: compact ? ['26%', '50%'] : ['40%', '70%'],
          avoidLabelOverlap: true,
          itemStyle: {
            borderRadius: 4,
            borderColor: '#fff',
            borderWidth: 2,
          },
          label: { show: false },
          emphasis: {
            label: { show: true, fontSize: compact ? 10 : 12, fontWeight: 'bold' },
          },
          data: chartData,
        },
      ],
    };
  };

  const getDemographicsBarOption = (
    distribution: Record<string, number>,
    title: string,
    entitiesByValue?: Record<string, DemographicsEntityRef[]>,
    compact = false,
  ) => {
    const categories = Object.keys(distribution);
    const values = Object.values(distribution);

    return {
      title: {
        text: title,
        left: 'center',
        textStyle: {
          fontSize: compact ? 11 : 14,
          fontWeight: 700,
          color: theme.palette.text.primary,
        },
      },
      tooltip: {
        trigger: 'axis',
        renderMode: 'html',
        formatter: (params: unknown) => {
          const p = Array.isArray(params) ? params[0] : params;
          if (!p || typeof p !== 'object') return '';
          const row = p as { name?: string; value?: number };
          const name = row.name ?? '';
          const v = row.value ?? 0;
          return buildDistributionTooltipHtml(name, v, null, entitiesByValue?.[name]);
        },
      },
      xAxis: {
        type: 'category',
        data: categories,
        axisLabel: {
          rotate: compact ? 25 : 30,
          interval: 0,
          fontSize: compact ? 10 : 12,
          color: theme.palette.text.primary,
        },
      },
      yAxis: {
        type: 'value',
        axisLabel: { color: theme.palette.text.secondary },
      },
      series: [
        {
          type: 'bar',
          data: values,
          itemStyle: { borderRadius: [4, 4, 0, 0] },
        },
      ],
    };
  };

  const renderAggregateChart = (
    label: string,
    agg: DashboardDemographicsAggregate,
    compact = false,
  ) => {
    const h = compact ? 220 : 320;
    const dist = distributionRecordFromAggregate(agg);
    const byVal = entitiesByValueFromAggregate(agg);
    if (dist && Object.keys(dist).length > 0) {
      return (
        <Paper variant="outlined" sx={{ p: compact ? 0.5 : 1, height: h }}>
          <ReactECharts
            option={
              chartType === 'pie'
                ? getDemographicsPieOption(dist, label, byVal, compact)
                : getDemographicsBarOption(dist, label, byVal, compact)
            }
            style={{ height: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        </Paper>
      );
    }

    const numericPie = mergeNumericBinsForPie(agg);
    if (numericPie) {
      return (
        <Paper variant="outlined" sx={{ p: compact ? 0.5 : 1, height: h }}>
          <ReactECharts
            option={getDemographicsPieOption(
              numericPie.distribution,
              label,
              numericPie.entitiesByValue,
              compact,
            )}
            style={{ height: '100%' }}
            opts={{ renderer: 'canvas' }}
          />
        </Paper>
      );
    }

    return (
      <Paper variant="outlined" sx={{ p: 2, minHeight: compact ? 80 : 120 }}>
        <Typography variant="caption" color="text.secondary">
          {label}: no chartable distribution (sample may be empty)
        </Typography>
      </Paper>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (demoData?.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">{demoData.error}</Alert>
      </Box>
    );
  }

  const isLeaf = demoData?.is_leaf === true;

  /* ── Leaf: attribute table only ───────────────────────────────────── */
  if (isLeaf && demoData?.attributes) {
    return (
      <Box>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <AssessmentIcon color="primary" />
            Entity attributes
            <Chip label="Leaf entity" size="small" color="info" sx={{ ml: 1 }} />
          </Typography>
          {demoData.entity_name && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {demoData.entity_name}
              {demoData.entity_type ? ` · ${demoData.entity_type}` : ''}
            </Typography>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <strong>Display name</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Key</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Data type</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Value</strong>
                  </TableCell>
                  <TableCell>
                    <strong>Description</strong>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {demoData.attributes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Typography variant="body2" color="text.secondary">
                        No attributes on this entity.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  demoData.attributes.map((row) => (
                    <TableRow key={row.name}>
                      <TableCell>{row.display_label}</TableCell>
                      <TableCell>{row.name}</TableCell>
                      <TableCell>{row.data_type}</TableCell>
                      <TableCell>
                        {row.value === null || row.value === undefined
                          ? '—'
                          : typeof row.value === 'object'
                            ? JSON.stringify(row.value)
                            : String(row.value)}
                      </TableCell>
                      <TableCell>{row.description ?? '—'}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    );
  }

  /* ── Non-leaf: pie/bar charts from groups ───────────────────────── */
  const hasGroups = groups && Object.keys(groups).length > 0;

  if (!isLeaf && !hasGroups) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">
          No matching descendant entities for category &quot;{effectiveCategory}&quot; under this entity,
          or no attribute aggregates yet.
        </Alert>
      </Box>
    );
  }

  const groupCounts: Record<string, number> = {};
  if (groups) {
    Object.entries(groups).forEach(([name, g]) => {
      groupCounts[name] = g.entity_count;
    });
  }

  const nFilteredUsable = tabAttributeBuckets.filteredUsable.length;
  const maxUsablePage = Math.max(1, Math.ceil(nFilteredUsable / PAGE_SIZE));
  const safeAttrPage = Math.min(attrPage, maxUsablePage);
  const usablePageSlice = tabAttributeBuckets.filteredUsable.slice(
    (safeAttrPage - 1) * PAGE_SIZE,
    safeAttrPage * PAGE_SIZE,
  );

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AssessmentIcon color="primary" />
            Demographics
            <Chip
              label={`${demoData?.total_matching_entities ?? 0} entities · ${effectiveCategory}`}
              size="small"
              color="default"
              sx={{ ml: 1 }}
            />
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {availableCategories.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel>Category filter</InputLabel>
                <Select
                  value={effectiveCategory}
                  label="Category filter"
                  onChange={(e: SelectChangeEvent) => onCategoryChange?.(e.target.value)}
                >
                  {availableCategories.map((cat) => (
                    <MenuItem key={cat.category} value={cat.category}>
                      {cat.category} ({cat.entity_count})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <ToggleButtonGroup
              value={chartType}
              exclusive
              onChange={(_, value) => value && setChartType(value)}
              size="small"
            >
              <ToggleButton value="pie" aria-label="pie chart">
                <PieChartIcon fontSize="small" />
              </ToggleButton>
              <ToggleButton value="bar" aria-label="bar chart">
                <BarChartIcon fontSize="small" />
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>
        </Box>

        {/* Collapsible summary: compact entity mix + top insights */}
        {Object.keys(groupCounts).length > 0 && (
          <Box
            sx={{
              mb: 2,
              px: 1.5,
              py: 1,
              borderRadius: 1,
              border: 1,
              borderColor: 'divider',
              bgcolor: 'action.hover',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 1,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                <Box component="span" fontWeight={700}>
                  Summary
                </Box>
                {' · '}
                {demoData?.total_matching_entities ?? 0} entities · {effectiveCategory}
                {sortedGroupNames.length > 0
                  ? ` · ${sortedGroupNames.length} type${sortedGroupNames.length === 1 ? '' : 's'}`
                  : ''}
              </Typography>
              <Tooltip title={summaryExpanded ? 'Collapse overview' : 'Expand entity mix & top insights'}>
                <IconButton
                  size="small"
                  onClick={() => setSummaryExpanded((v) => !v)}
                  aria-expanded={summaryExpanded}
                  aria-label={summaryExpanded ? 'Collapse summary' : 'Expand summary'}
                >
                  {summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Tooltip>
            </Box>
            <Collapse in={summaryExpanded}>
              <Grid container spacing={2} sx={{ mt: 0.5, alignItems: 'flex-start' }}>
                <Grid item xs={12} md={4}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Entity mix (by type)
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 0.5, height: 200 }}>
                    <ReactECharts
                      option={
                        chartType === 'pie'
                          ? getDemographicsPieOption(groupCounts, 'By type', undefined, true)
                          : getDemographicsBarOption(groupCounts, 'By type', undefined, true)
                      }
                      style={{ height: '100%', width: '100%' }}
                      opts={{ renderer: 'canvas' }}
                    />
                  </Paper>
                </Grid>
                <Grid item xs={12} md={8}>
                  <Typography variant="caption" fontWeight={700} color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Top insights (this entity type — highest specified coverage and variety)
                  </Typography>
                  <Grid container spacing={1}>
                    {tabAttributeBuckets.topInsights.map((attr) => (
                      <Grid item xs={12} sm={6} md={4} key={`insight-${attr.name}`}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            fontWeight={700}
                            noWrap
                            title={attr.display_label}
                            display="block"
                            sx={{ mb: 0.25 }}
                          >
                            {attr.display_label}
                          </Typography>
                          {renderAggregateChart(attr.display_label, attr.aggregate, true)}
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                  {tabAttributeBuckets.topInsights.length === 0 && (
                    <Typography variant="caption" color="text.secondary">
                      No attributes with specified values for this type (all &quot;Not Specified&quot; or empty).
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </Collapse>
          </Box>
        )}

        {groups && (
          <Box sx={{ width: '100%' }}>
            <Tabs
              value={Math.min(entityTypeSubTab, Math.max(0, sortedGroupNames.length - 1))}
              onChange={(_, v) => setEntityTypeSubTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Review entity type demographics"
              sx={{
                borderBottom: 1,
                borderColor: 'divider',
                minHeight: 48,
                '& .MuiTab-root': { minHeight: 48, alignItems: 'center' },
              }}
            >
              {sortedGroupNames.map((groupName, i) => {
                const g = groups[groupName];
                return (
                  <Tab
                    key={groupName}
                    id={`demographics-entity-tab-${i}`}
                    aria-controls={`demographics-entity-tabpanel-${i}`}
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <BusinessIcon fontSize="small" color="primary" sx={{ flexShrink: 0 }} />
                        <Typography component="span" variant="body2" fontWeight={600}>
                          {groupName}
                        </Typography>
                        <Chip
                          label={`${g.entity_count}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ height: 22 }}
                        />
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>

            <Box
              role="tabpanel"
              id="demographics-entity-tabpanel-active"
              sx={{ pt: 2 }}
            >
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
                <TextField
                  size="small"
                  placeholder="Search attributes…"
                  value={attributeSearch}
                  onChange={(e) => setAttributeSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 260, flex: '1 1 220px' }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={hideNoUsableSection}
                      onChange={(e) => setHideNoUsableSection(e.target.checked)}
                      size="small"
                    />
                  }
                  label="Hide no usable values section"
                />
              </Box>

              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                Attributes (with specified values)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                Top {TOP_INSIGHTS_COUNT} attributes appear in Summary above; this list avoids duplicating them.
              </Typography>
              <Grid container spacing={2}>
                {usablePageSlice.map((attr) => (
                  <Grid item xs={12} md={6} lg={4} key={attr.name}>
                    <Box sx={{ mb: 0.5, px: 0.5 }}>
                      <Typography
                        component="div"
                        variant="subtitle2"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ lineHeight: 1.3 }}
                      >
                        {attr.display_label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" display="block">
                        Total Count = {attr.sample_size}
                      </Typography>
                    </Box>
                    {renderAggregateChart(attr.display_label, attr.aggregate)}
                  </Grid>
                ))}
              </Grid>
              {nFilteredUsable === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                  No attributes match this search, or none have specified values for this type.
                </Typography>
              )}
              {nFilteredUsable > PAGE_SIZE && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                  <Pagination
                    count={maxUsablePage}
                    page={safeAttrPage}
                    onChange={(_, p) => setAttrPage(p)}
                    color="primary"
                    size="small"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}

              {!hideNoUsableSection && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Box sx={{ maxWidth: 520 }}>
                    <Box
                      role="button"
                      tabIndex={0}
                      aria-expanded={noUsableExpanded}
                      onClick={() => setNoUsableExpanded((v) => !v)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          setNoUsableExpanded((v) => !v);
                        }
                      }}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.75,
                        cursor: 'pointer',
                        py: 0.5,
                        pr: 1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' },
                      }}
                    >
                      {noUsableExpanded ? (
                        <ExpandLessIcon fontSize="small" color="action" />
                      ) : (
                        <ExpandMoreIcon fontSize="small" color="action" />
                      )}
                      <Typography variant="subtitle2" fontWeight={700} component="span">
                        Unspecified-only attributes
                      </Typography>
                      <Chip
                        label={tabAttributeBuckets.filteredUnusable.length}
                        size="small"
                        variant="outlined"
                        sx={{ height: 22 }}
                      />
                    </Box>
                    <Collapse in={noUsableExpanded}>
                      <Box sx={{ mt: 1, maxWidth: 480 }}>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          100% Not Specified or no chartable data — omitted from charts above.
                        </Typography>
                        <TableContainer
                          component={Paper}
                          variant="outlined"
                          sx={{ maxWidth: 480, width: '100%' }}
                        >
                          <Table size="small" sx={{ tableLayout: 'fixed' }}>
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ width: '72%' }}>Attribute</TableCell>
                                <TableCell align="right" sx={{ width: '28%' }}>
                                  Total Count
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {tabAttributeBuckets.filteredUnusable.map((attr) => (
                                <TableRow key={attr.name}>
                                  <TableCell
                                    sx={{
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                    title={attr.display_label}
                                  >
                                    {attr.display_label}
                                  </TableCell>
                                  <TableCell align="right">{attr.sample_size}</TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                        {tabAttributeBuckets.filteredUnusable.length === 0 && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                            None (or none match search).
                          </Typography>
                        )}
                      </Box>
                    </Collapse>
                  </Box>
                </>
              )}
            </Box>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default AttributeDemographicsTab;
